import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './constants';

const POLL_INTERVAL_MS = 30000;
const JTI_ADMIN_PROJECTS = new Set(['jti', 'jti-global', 'jti-sweden', 'admin']);

const notificationHeadline = (item) => {
  const actor = item.actor_name || 'Someone';
  const preview = (item.preview || '').trim().toLowerCase();
  if (preview.startsWith('status changed')) {
    return `${actor} changed the status of #${item.ticket_id}`;
  }
  return `${actor} commented on #${item.ticket_id}`;
};

const formatRelativeTime = (value) => {
  if (!value) return '';
  const parsed = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} d ago`;
  return parsed.toLocaleDateString();
};

const NotificationsBell = ({ projectKey }) => {
  const navigate = useNavigate();
  const { isAdmin, projectKey: authProjectKey } = useAuth();
  const canViewAdminSummary = Boolean(isAdmin && JTI_ADMIN_PROJECTS.has(authProjectKey || projectKey));

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryUsers, setSummaryUsers] = useState([]);
  const [summaryNotifications, setSummaryNotifications] = useState([]);
  const [summaryTotals, setSummaryTotals] = useState({ total: 0, unread_count: 0, user_count: 0 });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [summaryQuery, setSummaryQuery] = useState('');

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/notifications`, { withCredentials: true });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      // Stay silent in the header if the session is not ready yet.
    }
  }, []);

  const fetchAdminSummary = useCallback(async () => {
    if (!canViewAdminSummary) return;
    setSummaryLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/notifications/admin-summary`, {
        withCredentials: true,
      });
      setSummaryUsers(data.users || []);
      setSummaryNotifications(data.notifications || []);
      setSummaryTotals({
        total: data.total || 0,
        unread_count: data.unread_count || 0,
        user_count: data.user_count || 0,
      });
    } catch (error) {
      console.error('Failed to load notification summary', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [canViewAdminSummary]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    const onFocus = () => fetchNotifications();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchNotifications]);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const ticketPath = (item) => {
    const key = item.project_key || projectKey;
    return key ? `/${key}/support/${item.ticket_id}` : `/support/${item.ticket_id}`;
  };

  const handleOpenTicket = async (item) => {
    if (!item?.ticket_id) return;
    if (!item.is_read && item.id) {
      try {
        await axios.put(`${API_URL}/notifications/${item.id}/read`, {}, { withCredentials: true });
      } catch (error) {
        // Navigation still proceeds.
      }
    }
    handleClose();
    setSummaryOpen(false);
    navigate(ticketPath(item));
    fetchNotifications();
  };

  const handleMarkAllRead = async (event) => {
    event.stopPropagation();
    try {
      await axios.put(`${API_URL}/notifications/read-all`, {}, { withCredentials: true });
      await fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notifications as read', error);
    }
  };

  const handleOpenSummary = async (event) => {
    event.stopPropagation();
    handleClose();
    setSummaryOpen(true);
    setSelectedUserId(null);
    setSummaryQuery('');
    await fetchAdminSummary();
  };

  const filteredSummaryNotifications = useMemo(() => {
    const query = summaryQuery.trim().toLowerCase();
    return summaryNotifications.filter((item) => {
      if (selectedUserId != null && item.user_id !== selectedUserId) return false;
      if (!query) return true;
      const haystack = [
        item.recipient_name,
        item.recipient_email,
        item.actor_name,
        item.ticket_subject,
        item.preview,
        item.ticket_id != null ? `#${item.ticket_id}` : '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [summaryNotifications, selectedUserId, summaryQuery]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{ color: 'white', height: 32, width: 32 }}
          aria-label="Notifications"
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            overlap="circular"
          >
            {unreadCount > 0 ? (
              <NotificationsIcon sx={{ fontSize: 22 }} />
            ) : (
              <NotificationsNoneIcon sx={{ fontSize: 22 }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: 'calc(100vw - 24px)',
            mt: 1,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {canViewAdminSummary && (
              <Button
                size="small"
                onClick={handleOpenSummary}
                startIcon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                All users
              </Button>
            )}
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllRead} sx={{ textTransform: 'none' }}>
                Mark all as read
              </Button>
            )}
          </Box>
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 420, overflow: 'auto' }}>
            {notifications.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => handleOpenTicket(item)}
                alignItems="flex-start"
                sx={{
                  bgcolor: item.is_read ? 'transparent' : 'action.hover',
                  borderLeft: item.is_read ? '3px solid transparent' : '3px solid',
                  borderLeftColor: item.is_read ? 'transparent' : 'primary.main',
                }}
              >
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: item.is_read ? 500 : 700 }}>
                      {notificationHeadline(item)}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.ticket_subject ? `${item.ticket_subject} — ` : ''}
                        {item.preview || 'New discussion message'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(item.created_at)}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>

      <Dialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box>
            <Typography variant="h6">JTI notification summary</Typography>
            <Typography variant="body2" color="text.secondary">
              All support discussion notifications across JTI users
            </Typography>
          </Box>
          <IconButton onClick={() => setSummaryOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`${summaryTotals.user_count} users`} />
            <Chip label={`${summaryTotals.total} notifications`} />
            <Chip
              color={summaryTotals.unread_count ? 'warning' : 'default'}
              label={`${summaryTotals.unread_count} unread`}
            />
          </Box>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by user, ticket, or message"
            value={summaryQuery}
            onChange={(event) => setSummaryQuery(event.target.value)}
            sx={{ mb: 2 }}
          />
          {summaryLoading ? (
            <Typography variant="body2" color="text.secondary">Loading summary…</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2 }}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 520, overflow: 'auto' }}>
                <List dense sx={{ py: 0 }}>
                  <ListItemButton
                    selected={selectedUserId == null}
                    onClick={() => setSelectedUserId(null)}
                  >
                    <ListItemText
                      primary="All users"
                      secondary={`${summaryTotals.unread_count} unread`}
                    />
                  </ListItemButton>
                  {summaryUsers.map((row) => (
                    <ListItemButton
                      key={row.user_id}
                      selected={selectedUserId === row.user_id}
                      onClick={() => setSelectedUserId(row.user_id)}
                    >
                      <ListItemText
                        primary={row.name || row.email || `User #${row.user_id}`}
                        secondary={`${row.unread} unread · ${row.total} total`}
                        primaryTypographyProps={{ noWrap: true }}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 520, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Recipient</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>Ticket</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSummaryNotifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary">
                            No notifications for this view
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSummaryNotifications.map((item) => (
                        <TableRow
                          key={item.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleOpenTicket(item)}
                        >
                          <TableCell>
                            <Typography variant="body2">{item.recipient_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.recipient_email}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.actor_name}</TableCell>
                          <TableCell>
                            #{item.ticket_id}
                            {item.ticket_subject ? ` · ${item.ticket_subject}` : ''}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2" noWrap title={item.preview}>
                              {item.preview || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={item.is_read ? 'Read' : 'Unread'}
                              color={item.is_read ? 'default' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>{formatRelativeTime(item.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default NotificationsBell;
